import { useEffect, useState } from "react";
import api, { apiError, fmtNum, fmtUSD } from "../../lib/api";
import { toast } from "sonner";

export default function Transfers() {
  const [listings, setListings] = useState(null);
  const [mine, setMine] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ holding_id: "", units: "", asking_price_per_unit: "", note: "" });
  const [interestFor, setInterestFor] = useState(null);
  const [interest, setInterest] = useState({ units: "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/transfers/listings").then((r) => setListings(r.data)).catch(() => setListings([]));
    api.get("/transfers/mine").then((r) => setMine(r.data)).catch(() => setMine({ listings: [], interests: [] }));
    api.get("/portfolio").then((r) => setHoldings(r.data.holdings)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createListing = async () => {
    setBusy(true);
    try {
      await api.post("/transfers/listings", {
        holding_id: form.holding_id,
        units: Number(form.units),
        asking_price_per_unit: form.asking_price_per_unit ? Number(form.asking_price_per_unit) : null,
        note: form.note || null,
      });
      toast.success("Position offered for transfer. Solera will contact interested members and facilitate the transfer.");
      setShowForm(false);
      setForm({ holding_id: "", units: "", asking_price_per_unit: "", note: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendInterest = async () => {
    setBusy(true);
    try {
      await api.post(`/transfers/listings/${interestFor.listing_id}/interest`, {
        units: Number(interest.units), message: interest.message || null,
      });
      toast.success("Interest recorded. Solera facilitates every transfer and will contact you directly.");
      setInterestFor(null);
      setInterest({ units: "", message: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id) => {
    try {
      await api.post(`/transfers/listings/${id}/withdraw`);
      toast.success("Listing withdrawn.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!listings || !mine) return <span className="t-label">Loading</span>;

  return (
    <div className="rise" data-testid="transfers-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <span className="t-label">Transfers</span>
          <h1 className="t-h1 text-bone mt-3">Positions offered by members</h1>
          <p className="t-small text-muted-s mt-2 measure">
            Members may offer part of a position for transfer to other members. Solera reads the
            underlying agreement, obtains issuer confirmation and facilitates every transfer
            directly. Sellers are never named.
          </p>
        </div>
        <button className="btn-ghost" onClick={() => setShowForm(!showForm)} data-testid="offer-position-button">
          Offer a position
        </button>
      </div>

      {showForm && (
        <div className="hairline mt-8 pt-8 max-w-xl" data-testid="listing-form">
          <span className="t-label">Offer a position for transfer</span>
          <div className="mt-4 space-y-3">
            <select className="input-solera" value={form.holding_id}
              onChange={(e) => setForm({ ...form, holding_id: e.target.value })} data-testid="listing-holding-select">
              <option value="">Select a position</option>
              {holdings.map((h) => (
                <option key={h.holding_id} value={h.holding_id}>
                  {h.company}, {h.share_class}, {fmtNum(h.units)} units
                </option>
              ))}
            </select>
            <input className="input-solera fig" type="number" placeholder="Units to offer"
              value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} data-testid="listing-units-input" />
            <input className="input-solera fig" type="number" step="0.01" placeholder="Asking price per unit, USD (optional)"
              value={form.asking_price_per_unit} onChange={(e) => setForm({ ...form, asking_price_per_unit: e.target.value })} data-testid="listing-price-input" />
            <textarea className="input-solera" rows={2} placeholder="Note (optional)"
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="listing-note-input" />
            <button className="btn-primary" disabled={busy || !form.holding_id || !form.units}
              onClick={createListing} data-testid="listing-submit-button">Offer for transfer</button>
          </div>
        </div>
      )}

      <table className="table-solera mt-10" data-testid="listings-table">
        <thead>
          <tr><th>Position</th><th>Class</th><th className="num">Units offered</th><th className="num">Asking</th><th>Note</th><th></th></tr>
        </thead>
        <tbody>
          {listings.map((l) => (
            <tr key={l.listing_id} data-testid={`listing-row-${l.listing_id}`}>
              <td className="text-bone">{l.company}</td>
              <td className="text-muted-s">{l.share_class}</td>
              <td className="num text-bone">{fmtNum(l.units)}</td>
              <td className="num text-bone">{l.asking_price_per_unit ? l.asking_price_per_unit.toFixed(2) : "On request"}</td>
              <td className="text-muted-s t-small measure">{l.note || ""}</td>
              <td className="text-right whitespace-nowrap">
                {l.mine ? (
                  <span className="t-label">Your listing</span>
                ) : l.my_interest ? (
                  <span className="t-label" data-testid={`interest-recorded-${l.listing_id}`}>Interest recorded</span>
                ) : (
                  <button onClick={() => { setInterestFor(l); setInterest({ units: String(l.units), message: "" }); }}
                    className="t-label hover:!text-[#E8E6E1] transition-colors duration-150"
                    data-testid={`express-interest-button-${l.listing_id}`}>Express interest</button>
                )}
              </td>
            </tr>
          ))}
          {listings.length === 0 && (
            <tr><td colSpan={6} className="text-muted-s t-small py-8">No positions are currently offered.</td></tr>
          )}
        </tbody>
      </table>

      {interestFor && (
        <div className="hairline mt-8 pt-8 max-w-xl" data-testid="interest-form">
          <span className="t-label">Express interest, {interestFor.company}</span>
          <p className="t-cap text-muted-s mt-2 measure">
            Expressing interest is not binding and is not an offer. Solera will contact you to
            discuss the transfer. The seller is not told who you are.
          </p>
          <div className="mt-4 space-y-3">
            <input className="input-solera fig" type="number" placeholder={`Units, up to ${fmtNum(interestFor.units)}`}
              value={interest.units} onChange={(e) => setInterest({ ...interest, units: e.target.value })} data-testid="interest-units-input" />
            <textarea className="input-solera" rows={2} placeholder="Message to Solera (optional)"
              value={interest.message} onChange={(e) => setInterest({ ...interest, message: e.target.value })} data-testid="interest-message-input" />
            <div className="flex gap-3">
              <button className="btn-ghost" onClick={() => setInterestFor(null)} data-testid="interest-cancel-button">Cancel</button>
              <button className="btn-primary" disabled={busy || !interest.units} onClick={sendInterest} data-testid="interest-submit-button">
                Record interest
              </button>
            </div>
          </div>
        </div>
      )}

      {(mine.listings.length > 0 || mine.interests.length > 0) && (
        <div className="mt-16 grid md:grid-cols-2 gap-12">
          <div>
            <span className="t-label">Your listings</span>
            <table className="table-solera mt-4" data-testid="my-listings-table">
              <thead><tr><th>Position</th><th className="num">Units</th><th>Status</th><th className="num">Interest</th><th></th></tr></thead>
              <tbody>
                {mine.listings.map((l) => (
                  <tr key={l.listing_id}>
                    <td className="text-bone">{l.company}</td>
                    <td className="num text-bone">{fmtNum(l.units)}</td>
                    <td className="text-muted-s capitalize">{l.status.replace("_", " ")}</td>
                    <td className="num text-bone">{l.interest_count}</td>
                    <td className="text-right">
                      {l.status === "open" && (
                        <button onClick={() => withdraw(l.listing_id)} className="t-label hover:!text-[#E8E6E1]"
                          data-testid={`withdraw-button-${l.listing_id}`}>Withdraw</button>
                      )}
                    </td>
                  </tr>
                ))}
                {mine.listings.length === 0 && <tr><td colSpan={5} className="text-muted-s t-small py-6">None.</td></tr>}
              </tbody>
            </table>
          </div>
          <div>
            <span className="t-label">Your interests</span>
            <table className="table-solera mt-4" data-testid="my-interests-table">
              <thead><tr><th>Position</th><th className="num">Units</th><th>Status</th></tr></thead>
              <tbody>
                {mine.interests.map((i) => (
                  <tr key={i.interest_id}>
                    <td className="text-bone">{i.company}</td>
                    <td className="num text-bone">{fmtNum(i.units)}</td>
                    <td className="text-muted-s capitalize">{i.status.replace("_", " ")}</td>
                  </tr>
                ))}
                {mine.interests.length === 0 && <tr><td colSpan={3} className="text-muted-s t-small py-6">None.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
